/**
 * SettlementUploadPage Component
 * 정산서 업로드 페이지
 *
 * 협력사/라이더 정산서 업로드 및 이력 관리
 */
import React, { useState, useRef, useCallback } from 'react';
import { useSettlementUpload } from '../hooks/useSettlementUpload';
import { SettlementUploadType, SETTLEMENT_UPLOAD_TYPE_LABELS } from '../types/settlementUpload.types';
import { usePagination } from '@/shared/hooks/usePagination';
import { useTableSort } from '@/shared/hooks/useTableSort';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';
import '../styles/SettlementUploadPage.css';

const ITEMS_PER_PAGE = 15;

// 테이블 헤더 정의
const TABLE_HEADERS = [
    { key: 'no', label: 'No', sortable: false, width: '60px' },
    { key: 'uploadType', label: '구분', sortable: true, width: '120px' },
    { key: 'uploadDate', label: '업로드일자', sortable: true, width: '140px' },
    { key: 'applyDate', label: '적용일자', sortable: true, width: '120px' },
    { key: 'settlementPeriod', label: '정산기간', sortable: false, width: '180px' },
    { key: 'supplierCount', label: '협력사수', sortable: true, width: '100px' },
    { key: 'riderCount', label: '라이더수', sortable: true, width: '100px' },
    { key: 'deliveryFee', label: '배달료', sortable: true, width: '130px' },
    { key: 'additionalSettlement', label: '후정산', sortable: true, width: '120px' },
    { key: 'managementFee', label: '관리비', sortable: true, width: '120px' },
    { key: 'supplyAmount', label: '공급가액', sortable: true, width: '130px' },
    { key: 'vatAmount', label: '부가세액', sortable: true, width: '120px' },
    { key: 'hourlyInsurance', label: '시간제보험료', sortable: true, width: '130px' },
    { key: 'isApplied', label: '적용여부', sortable: true, width: '100px' },
    { key: 'actions', label: '관리', sortable: false, width: '100px' },
] as const;

const SettlementUploadPage: React.FC = () => {
    // ======== Hooks ========
    const {
        loading,
        uploadLoading,
        applyLoading,
        histories,
        uploadSettlement,
        applyUpload,
        cancelUpload,
    } = useSettlementUpload();

    // ======== State ========
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState<{ type: 'apply' | 'cancel'; uploadId: string } | null>(null);

    // 파일 입력 refs
    const partnerFileInputRef = useRef<HTMLInputElement>(null);
    const riderFileInputRef = useRef<HTMLInputElement>(null);

    // 정렬 훅
    const {
        sortConfig,
        sortedData: sortedHistories,
        handleSort
    } = useTableSort({
        data: histories || [],
        numericColumns: ['supplierCount', 'riderCount', 'deliveryFee', 'additionalSettlement', 'managementFee', 'supplyAmount', 'vatAmount', 'hourlyInsurance'],
        dateColumns: ['uploadDate', 'applyDate']
    });

    // 페이지네이션 훅
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
    } = usePagination({ items: sortedHistories, itemsPerPage: ITEMS_PER_PAGE });

    // ======== Handlers ========

    /** 협력사 정산서 업로드 버튼 클릭 */
    const handlePartnerUploadClick = useCallback(() => {
        partnerFileInputRef.current?.click();
    }, []);

    /** 라이더 정산서 업로드 버튼 클릭 */
    const handleRiderUploadClick = useCallback(() => {
        riderFileInputRef.current?.click();
    }, []);

    /** 파일 선택 핸들러 */
    const handleFileChange = useCallback(async (
        event: React.ChangeEvent<HTMLInputElement>,
        uploadType: SettlementUploadType
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;

        await uploadSettlement(uploadType, file);

        // 파일 입력 초기화
        event.target.value = '';
    }, [uploadSettlement]);

    /** 적용 버튼 클릭 */
    const handleApplyClick = useCallback((uploadId: string) => {
        setConfirmAction({ type: 'apply', uploadId });
        setShowConfirmModal(true);
    }, []);

    /** 취소 버튼 클릭 */
    const handleCancelClick = useCallback((uploadId: string) => {
        setConfirmAction({ type: 'cancel', uploadId });
        setShowConfirmModal(true);
    }, []);

    /** 확인 모달 확인 */
    const handleConfirm = useCallback(async () => {
        if (!confirmAction) return;

        if (confirmAction.type === 'apply') {
            await applyUpload(confirmAction.uploadId);
        } else {
            await cancelUpload(confirmAction.uploadId);
        }

        setShowConfirmModal(false);
        setConfirmAction(null);
    }, [confirmAction, applyUpload, cancelUpload]);

    /** 확인 모달 취소 */
    const handleModalCancel = useCallback(() => {
        setShowConfirmModal(false);
        setConfirmAction(null);
    }, []);

    /** 금액 포맷팅 */
    const formatAmount = (amount: number | undefined): string => {
        if (amount === undefined || amount === null) return '-';
        return amount.toLocaleString('ko-KR');
    };

    // ======== Render - 로딩 상태 ========
    if (loading && histories.length === 0) {
        return <Loading message="업로드 이력을 불러오는 중..." />;
    }

    if (uploadLoading) {
        return <Loading message="정산서를 업로드하는 중..." />;
    }

    if (applyLoading) {
        return <Loading message="정산서를 적용하는 중..." />;
    }

    // ======== Render - 메인 UI ========
    return (
        <div className="settlement-upload-page">

            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">정산서 업로드</h1>
            </div>

            {/* 업로드 버튼 영역 */}
            <div className="card upload-card">
                <div className="card-body">
                    <div className="upload-button-group">
                        <div className="upload-button-wrapper">
                            <button
                                className="btn btn-primary upload-btn"
                                onClick={handleRiderUploadClick}
                                disabled={uploadLoading}
                            >
                                <i className="fas fa-file-excel" />
                                라이더 주정산 업로드
                            </button>
                            <p className="upload-hint">라이더 주정산내역 엑셀 파일을 업로드합니다.</p>
                        </div>
                        <div className="upload-button-wrapper">
                            <button
                                className="btn btn-secondary upload-btn"
                                onClick={handlePartnerUploadClick}
                                disabled={uploadLoading}
                            >
                                <i className="fas fa-file-excel" />
                                협력사 주정산 업로드
                            </button>
                            <p className="upload-hint">협력사 주정산내역 엑셀 파일을 업로드합니다.</p>
                        </div>
                    </div>

                    {/* 숨겨진 파일 입력 */}
                    <input
                        ref={partnerFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'PARTNER')}
                        disabled={uploadLoading}
                    />
                    <input
                        ref={riderFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'RIDER')}
                        disabled={uploadLoading}
                    />
                </div>
            </div>

            {/* 업로드 안내 */}
            <div className="upload-info-section">
                <div className="info-item">
                    <i className="fas fa-info-circle" />
                    <span>업로드 순서: 라이더 주정산을 먼저 업로드한 후, 협력사 주정산을 업로드해주세요.</span>
                </div>
                <div className="info-item">
                    <i className="fas fa-exclamation-triangle" />
                    <span>적용 후에는 취소할 수 없습니다. 신중하게 적용해주세요.</span>
                </div>
            </div>

            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{sortedHistories.length.toLocaleString()}건</strong>
            </div>

            {/* 테이블 섹션 */}
            <div className="table-container settlement-upload-table-container">
                <table className="supplier-selection-table sortable-table">
                    <thead>
                        <tr>
                            {TABLE_HEADERS.map(header => {
                                const isSorted = sortConfig.columnKey === header.key;
                                const direction = isSorted ? sortConfig.direction : null;

                                return (
                                    <th
                                        key={header.key}
                                        style={{ width: header.width, minWidth: header.width }}
                                        className={header.sortable ? 'sortable-header' : ''}
                                        onClick={() => header.sortable && handleSort(header.key)}
                                    >
                                        <span className="header-content">
                                            {header.label}
                                            {header.sortable && <SortIcon direction={direction} />}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedHistories.length === 0 ? (
                            <tr>
                                <td colSpan={TABLE_HEADERS.length} className="text-center text-gray-600">
                                    업로드 이력이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((history, index) => (
                                <tr key={history.uploadId} className={history.isApplied ? 'row-applied' : ''}>
                                    <td className="text-center">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                    <td className="text-center">
                                        <span className={`badge badge-${history.uploadType === 'PARTNER' ? 'partner' : 'rider'}`}>
                                            {history.displayName || SETTLEMENT_UPLOAD_TYPE_LABELS[history.uploadType]}
                                        </span>
                                    </td>
                                    <td className="text-center">{history.uploadDate}</td>
                                    <td className="text-center">{history.applyDate || '-'}</td>
                                    <td className="text-center">
                                        {history.settlementStartDate} ~ {history.settlementEndDate}
                                    </td>
                                    <td className="text-right">{formatAmount(history.supplierCount)}</td>
                                    <td className="text-right">{formatAmount(history.riderCount)}</td>
                                    <td className="text-right">{formatAmount(history.deliveryFee)}</td>
                                    <td className="text-right">{formatAmount(history.additionalSettlement)}</td>
                                    <td className="text-right">{formatAmount(history.managementFee)}</td>
                                    <td className="text-right">{formatAmount(history.supplyAmount)}</td>
                                    <td className="text-right">{formatAmount(history.vatAmount)}</td>
                                    <td className="text-right">{formatAmount(history.hourlyInsurance)}</td>
                                    <td className="text-center">
                                        <span className={`status-badge ${history.isApplied ? 'status-applied' : 'status-pending'}`}>
                                            {history.isApplied ? '적용' : '대기'}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        {!history.isApplied && (
                                            <div className="action-buttons">
                                                {history.canApply !== false ? (
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handleApplyClick(history.uploadId)}
                                                        disabled={applyLoading}
                                                    >
                                                        적용
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        disabled
                                                        title="더 최신 업로드가 이미 적용되어 적용할 수 없습니다"
                                                    >
                                                        적용 불가
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />

            {/* 확인 모달 */}
            <ConfirmModal
                isOpen={showConfirmModal}
                title={confirmAction?.type === 'apply' ? '정산서 적용' : '업로드 취소'}
                message={
                    confirmAction?.type === 'apply'
                        ? '정산서를 적용하시겠습니까? 적용 후에는 취소할 수 없습니다.'
                        : '업로드를 취소하시겠습니까?'
                }
                confirmText={confirmAction?.type === 'apply' ? '적용' : '취소'}
                cancelText="닫기"
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
            />
        </div>
    );
};

export default SettlementUploadPage;
